/* eslint-disable no-underscore-dangle */
import React, { useState } from "react"
import { Link } from "gatsby"

import { useLocation } from "store/contexts/location"

import {
  Container,
  GroupedSections,
  GroupedSection,
  GroupedSectionTitle,
  GroupedSectionLink,
  GroupedSectionExternalLink,
  GroupedSectionItems,
  ExpandIcon,
  IconSection,
  Icons,
  SocialIcon,
  Copyright,
  Disclaimer,
  MBGLogoContainer,
  MBGLogoFull,
  MBGLogoShort,
  FooterContent,
  BottomRow,
  BottomRowContent,
  ButtonLink,
  UtilityLinks,
  CookieSettingsButton,
} from "./styles"

const groupedSections = [
  {
    title: "Read",
    items: [
      { title: "Health", link: "/health" },
      { title: "Food", link: "/food" },
      { title: "Movement", link: "/movement" },
      { title: "Lifestyle", link: "/lifestyle" },
      { title: "Mindfulness", link: "/mindfulness" },
      { title: "Relationships", link: "/relationships" },
      { title: "Planet", link: "/planet" },
    ],
  },
  {
    title: "Learn",
    items: [
      {
        title: "Health Coaching+",
        link: "https://institute.mindbodygreen.com/health-coach-certification",
      },
      {
        title: "nutrition & longevity+",
        link: "https://institute.mindbodygreen.com/nutrition-longevity",
      },
      { title: "Peri/menopause+", link: "https://institute.mindbodygreen.com/peri-menopause" },
    ],
  },
  {
    title: "Listen",
    items: [
      {
        title: "mindbodygreen podcast",
        link: "https://podcasts.apple.com/us/podcast/the-mindbodygreen-podcast/id1246494475",
      },
      {
        title: "clean beauty school podcast",
        link: "https://podcasts.apple.com/us/podcast/clean-beauty-school/id1562146692",
      },
    ],
  },
  {
    title: "Shop",
    items: [
      {
        title: "Manage Subscriptions",
        link: "http://shop.mindbodygreen.com/customer_authentication/login?return_to=/a/account/shopify-login",
      },
      { title: "Track Orders", link: "https://account.mindbodygreen.com" },
      {
        title: "Take the quiz",
        link: "https://shop.mindbodygreen.com/pages/mindbodygreen-supplements-quiz",
      },
      { title: "Contact", link: "/contact" },
      { title: "About Us", link: "/about" },
    ],
  },
]

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [expandedSections, setExpandedSections] = useState({})

  const { location } = useLocation()
  const isHomePage =
    location.pathname === "/" ||
    location.pathname === "/test-home-page" ||
    location.pathname === "/test/home-page-with-dynamic-content"

  const toggleSection = (sectionTitle) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }))
  }

  return (
    <Container id="footer">
      <FooterContent>
        {/* Links */}
        <GroupedSections>
          {groupedSections.map((section) => (
            <GroupedSection
              key={section.title}
              aria-expanded={expandedSections[section.title] || false}
            >
              <GroupedSectionTitle
                tabIndex={0}
                onClick={() => toggleSection(section.title)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    toggleSection(section.title)
                  }
                }}
              >
                {section.title}
                <ExpandIcon isExpanded={expandedSections[section.title]}>+</ExpandIcon>
              </GroupedSectionTitle>
              <GroupedSectionItems isExpanded={expandedSections[section.title]}>
                {section.items.map((item) =>
                  item.link.startsWith("http") ? (
                    <GroupedSectionExternalLink
                      key={item.title}
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.title}
                    </GroupedSectionExternalLink>
                  ) : (
                    <GroupedSectionLink key={item.title} to={item.link}>
                      {item.title}
                    </GroupedSectionLink>
                  ),
                )}
              </GroupedSectionItems>
            </GroupedSection>
          ))}
        </GroupedSections>

        {/* Social Icons */}
        <IconSection>
          <Icons>
            <a href="https://www.instagram.com/mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-instagram-footer"
                width={30}
                height={30}
                title="mindbodygreen on Instagram"
              />
            </a>
            <a href="https://www.facebook.com/mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-facebook-footer"
                width={30}
                height={30}
                title="mindbodygreen on Facebook"
              />
            </a>
            <a href="https://twitter.com/mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-twitter-footer"
                width={30}
                height={30}
                title="mindbodygreen on Twitter"
              />
            </a>
            <a href="https://www.pinterest.com/mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-pinterest-footer"
                width={30}
                height={30}
                title="mindbodygreen on Pinterest"
              />
            </a>
            <a href="https://www.youtube.com/mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-youtube-footer"
                width={30}
                height={30}
                title="mindbodygreen on YouTube"
              />
            </a>
            <a href="https://www.tiktok.com/@mindbodygreen" target="_blank" rel="noreferrer">
              <SocialIcon
                id="social-tiktok"
                width={30}
                height={30}
                title="mindbodygreen on TikTok"
              />
            </a>
          </Icons>

          <React.Suspense>
            <Copyright>
              &copy; 2009 - {currentYear} mindbodygreen LLC. All rights reserved.
            </Copyright>
          </React.Suspense>
        </IconSection>
      </FooterContent>

      <BottomRow>
        <MBGLogoContainer>
          <MBGLogoShort id="mbg-logo-short" title="mindbodygreen" viewBox="0 0 500 500" />
          <MBGLogoFull
            id="home-page-title"
            title="mindbodygreen"
            width="100%"
            height="100%"
            viewBox="0 0 837 143"
          />
        </MBGLogoContainer>

        <BottomRowContent>
          <Disclaimer id="footer_disclaimer" isHomePage={isHomePage}>
            *These statements have not been evaluated by the Food and Drug Administration. This
            product is not intended to diagnose, treat, cure or prevent any disease.
          </Disclaimer>
          <UtilityLinks>
            <Link to="/jobs">Jobs</Link>
            <Link to="/terms-of-sale">Terms of Sale</Link>
            <Link to="/terms-of-use">Terms of Use</Link>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <a
              href="https://help.mindbodygreen.com/your-account-start-your-return-exchange-BktbjS_Hzl"
              target="_blank"
              rel="noreferrer"
            >
              Return Policy
            </a>
            <Link to="/accessibility">Accessibility</Link>
            <Link to="/privacy">Ad Choices</Link>
            <Link to="/data-collection">Data Collection</Link>
            <Link to="/editorial-process">Editorial Process</Link>
            <CookieSettingsButton type="button" className="optanon-show-settings">
              Cookie Settings
            </CookieSettingsButton>
          </UtilityLinks>
        </BottomRowContent>
      </BottomRow>
    </Container>
  )
}

export default Footer
